import { AxiosError, AxiosRequestConfig } from "axios";
import { HTTP_HEADER_AUTHORIZATION } from "./constants";
import { DecoratedHttpClient } from "./decorated-http-client";
import { HttpClient, TokenRefresher } from "./types";

/**
 * A Token Handling HTTP Client is intended to abstract the concern of properly
 * injecting a bearer token in each outgoing request.
 *
 * It also provides an optional feature to update the token with a fresh token
 * in case a request fails with a `410 Unauthorized` HTTP status code. Thus,
 * any subsequent requests made will be done with this fresh token. This
 * features is enabled by passing in a token refresher function as a constructor
 * argument.
 *
 * This HTTP client is especially powerful when composed with one of the other
 * clients in this library.
 *
 * @example
 *
 * ```js
 * import { TokenHandlingHttpClient } from "@ftdr/http-utils";
 *
 * const httpClient = new TokenHandlingHttpClient(
 *   "a-super-secret-token",
 *   () => Promise.resolve("a-fresh-super-secret-token"),
 * )
 *
 * try {
 *   const result = await httpClient.get("https://www.example.com");
 * } catch(error) {
 *   if (error.response.status === 401) {
 *     // some logic to re-try the request as the token is not refreshed
 *   }
 * }
 * ```
 */
export class TokenHandlingHttpClient<
  C extends AxiosRequestConfig = AxiosRequestConfig
> extends DecoratedHttpClient<C> {
  /**
   * Private field to temporarily hold the pending token refresher promise.
   *
   * This is used as a mechanism to not trigger more than one token refresh
   * calls at one, even if there are mre than one failing requests being
   * made by this HTTP client.
   *
   * When a request fails due to an invalid token and requires a token refresh,
   * the token refresher function is called and the resulting promise
   * is stored in this field. While this promise is still pending, any
   * subsequent requests initiated via this HTTP client, which would also fail
   * due to the invalid token, are made to await this same promise, instead of
   * triggering any more token refresher calls for them.
   */
  private tokenRefresherPromise: Promise<string> | null = null;

  /**
   * Construct a `TokenHandlingHttpClient` with some configuration parameters.
   *
   * @param token A bearer token to be set in the Authorization HTTP header
   *              of all outgoing requests that are initiated using this HTTP
   *              client instance.
   *
   * @param getFreshToken A function to get a fresh token when a request fails
   *                      with a 401 error.
   *
   * @param httpClient An optional HttpClient instance. Use this to compose
   *                   functionality of multiple HttpClient instances into a
   *                   single instance.
   */
  constructor(
    private token: string,
    private getFreshToken?: TokenRefresher,
    httpClient?: HttpClient
  ) {
    super(httpClient);
    this.interceptors.request.use(this.requestInterceptor);
    this.interceptors.response.use(undefined, this.responseInterceptor);
  }

  /**
   * This request interceptor sets the token in to the `Authorization` HTTP header
   * in the outgoing request.
   *
   * @param config The Axios config object with which a request is being
   * initiated.
   */
  private requestInterceptor = (config: C) => {
    if (config.headers) {
      config.headers[HTTP_HEADER_AUTHORIZATION] = `Bearer ${this.token}`;
    }
    return config;
  };

  /**
   * This response interceptor is responsible for checking if the response was
   * a 401 error response. If so it tries to:
   * 1. Checks if we're set up to get a fresh token. If not then simply reject
   *    the original promise.
   * 2. Get a fresh token.
   *
   * @param error The Axios error object
   */
  private responseInterceptor = async (error: AxiosError) => {
    if (error.response?.status === 401 && this.getFreshToken) {
      if (this.tokenRefresherPromise === null) {
        this.tokenRefresherPromise = this.getFreshToken();
      }

      try {
        this.token = await this.tokenRefresherPromise;
      } catch (tokenRefreshError) {
        return Promise.reject(new TokenRefreshError(tokenRefreshError, error));
      } finally {
        this.tokenRefresherPromise = null;
      }
    }
    return Promise.reject(error);
  };
}

/**
 * An error that represents failure to fetch the refresh token.
 * It includes the original token refresh error encountered as well as the
 * http error for which the token refresh was being performed.
 */
export class TokenRefreshError extends Error {
  static errorMessage = "Failed to refresh token";
  tokenRefreshError: unknown | null = null;
  httpError: unknown | null = null;

  /**
   * @param tokenRefreshError The original error encountered on trying to
   * refresh the token.
   *
   * @param httpError The original HTTP error for which the token refresh was
   * being performed.
   */
  constructor(tokenRefreshError?: unknown, httpError?: unknown) {
    super(TokenRefreshError.errorMessage);
    this.tokenRefreshError = tokenRefreshError;
    this.httpError = httpError;

    /**
     * @see {@link https://github.com/facebook/jest/issues/8279#issuecomment-539775425}
     */
    Object.setPrototypeOf(this, TokenRefreshError.prototype);
  }
}
